import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import ProductSwiper from "@components/swiper/ProductSwiper";
import { useTheme } from "@emotion/react";
import ColorBall from "@components/item/ColorBall";
import { useNavigate, useParams } from "react-router-dom";
import useUserInfoStore from "@store/useUserInfoStore";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";

import { Flex } from "antd";
import BasicButton from "@components/button/BasicButton";
import Image from "@components/item/Image";
import BasicDrawer from "@components/drawer/BasicDrawer";
import { useApiRequest } from "@utils/ApiRequest";
import { queryClient } from "@libs/reactQuery/queryClient";
import { useMutation } from "@tanstack/react-query";
import { formatDate } from "@utils/functions";

type Props = {
  productInfo: any;
};

const StockTemp = (props: Props) => {
  const theme = useTheme();
  const [selectedColor, setSelectedColor] = useState(null);

  const { productInfo } = props;
  const [deleteModal, setDeleteModal] = useState(false);
  const navigate = useNavigate();
  const { userInfo } = useUserInfoStore();
  const [openDrawer, setOpenDrawer] = useState(false);
  const { id, status } = useParams();

  const { deleteProductStockTemp } = useApiRequest();
  const selectedColorItem = productInfo?.color_list?.find(
    (item: any) => item.color_id === selectedColor,
  );

  const { mutate: deleteProductMutate } = useMutation({
    mutationFn: () => deleteProductStockTemp(Number(productInfo?.product_id)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getProductInventorySimple"],
      });
      navigate("/capture-list", {
        replace: true,
      });
    },
  });

  // console.log("productInfo", productInfo);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSelectedColor(productInfo?.color_list?.[0]?.color_id);
  }, [productInfo]);

  const isUserProduct = userInfo?.id === productInfo?.seller_id;

  const handleMenuClick = () => {
    setOpenDrawer(true);
  };

  return (
    <Container>
      <EditButtonContainer>
        {isUserProduct && (
          <>
            <EditMenuButton>
              <StyledImage
                onClick={handleMenuClick}
                src={theme.image.dot_icon.default}
              />
            </EditMenuButton>
          </>
        )}
        <ProductSwiper imageList={productInfo?.product_image_list} />
      </EditButtonContainer>

      <BasicDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        wrapperPadding="0"
        padding="0">
        <ItemBox>
          <Item onClick={() => setDeleteModal(true)}>{"삭제하기"}</Item>
        </ItemBox>
      </BasicDrawer>

      <ProductInfoWrapper>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: "2rem" }}>
          <Data>{formatDate(productInfo?.updated_at)}</Data>

          <Flex align="center" gap={"0.5rem"}>
            <Data>임시저장</Data>
            <Image
              src={theme.image.danger_circle_icon.default}
              width="2rem"
              height="2rem"
            />
          </Flex>
        </Flex>

        <CategoryBox>
          <span>{productInfo?.category_ko}</span>
        </CategoryBox>

        <span>
          {productInfo?.product_name_ko ? productInfo?.product_name_ko : "-"}
        </span>

        <ConditionBox>
          <span className="label">{"컬러"}</span>
          <div className="color_box">
            {productInfo?.color_list?.map((item: any) => (
              <ColorBall
                color={item.hex_code}
                colorName={item.name}
                key={item.color_id}
                onClick={() => setSelectedColor(item.color_id)}
                active={item.color_id === selectedColor}
              />
            ))}
          </div>
        </ConditionBox>

        {selectedColorItem && (
          <ConditionBox>
            <span className="label">사이즈(수량)</span>

            <div className="color_box">
              {selectedColorItem?.size_list?.map((size: any) => (
                <SizeItem
                  className="size"
                  key={size.id}
                  disabled={size.amount === 0}>
                  {size.name} ({size.amount})
                </SizeItem>
              ))}
            </div>
          </ConditionBox>
        )}
      </ProductInfoWrapper>

      <ButtonWrapper>
        <BasicButton
          title="재고 등록"
          width="100%"
          transparent
          borderColor="#000"
          onClick={() => {
            navigate(
              `/stock-register/${productInfo.product_id}/${status}`,
            );
          }}
        />
        <BasicButton
          title="샤카라카에 상품 등록"
          width="100%"
          onClick={() =>
            navigate(`/register/${id}/${status}`, {
              state: {
                origin: "sale-temp",
                returnTo: `/product-detail/${id}/${status}`,
              },
            })
          }
        />
      </ButtonWrapper>

      <BasicModal
        open={deleteModal}
        setOpen={setDeleteModal}
        warning
        activeEvent={() => {
          deleteProductMutate(productInfo?.product_id);
          setDeleteModal(false);
        }}
        activeName="삭제"
        closeName="취소"
        padding="6rem 2rem 2rem 2rem">
        <ModalContent mainMessage="이 상품을 삭제하시겠습니까?" />
      </BasicModal>
    </Container>
  );
};

export default StockTemp;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  height: "calc(100dvh - 5.6rem)",
});

const ProductInfoWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  padding: "2rem",
  flex: 1,
}));

const Data = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
}));

const CategoryBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  color: theme.color.gray,
}));

const ConditionBox = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  color: theme.color.gray,

  ".label": {
    minWidth: "8rem",
  },

  ".color_box": {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
}));

const SizeItem = styled.span<{ disabled: boolean }>(({ theme, disabled }) => ({
  padding: "0.7rem",
  color: disabled ? theme.color.light_gray : theme.color.black,
  minHeight: "3rem",
}));

const EditButtonContainer = styled.div(({ theme }) => ({
  position: "relative",
  width: "100%",
}));

const EditMenuButton = styled.div(({ theme }) => ({
  position: "absolute",
  zIndex: 2,
  width: "100%",
  background: "linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)",
  height: "50px",
}));

const StyledImage = styled.img(({ theme }) => ({
  display: "flex",
  objectFit: "cover",
  position: "absolute",
  right: "1.6rem",
  top: "1rem",
  cursor: "pointer",
}));

const ItemBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

const Item = styled.button(({ theme }) => ({
  width: "100%",
  minHeight: "5.6rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1px solid ${theme.color.border_gray}`,

  "&:first-child": {
    border: "none",
  },

  "&:last-child": {
    color: theme.color.orange,
    justifyContent: "center",
  },
}));

const ButtonWrapper = styled.div(({ theme }) => ({
  padding: "1rem",
  display: "flex",
  gap: "1rem",
  width: "100%",
}));
